package br.com.vvrefeicoes.portal.feature.rmp

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import br.com.vvrefeicoes.portal.core.di.LocalAppGraph
import br.com.vvrefeicoes.portal.core.util.encodeBase64
import br.com.vvrefeicoes.portal.core.util.file.PickedFile
import br.com.vvrefeicoes.portal.core.util.openFileInBrowser
import br.com.vvrefeicoes.portal.core.util.savePdf
import br.com.vvrefeicoes.portal.domain.model.RmpApproval
import br.com.vvrefeicoes.portal.domain.model.RmpRequestAttachment
import br.com.vvrefeicoes.portal.domain.model.RmpTestAttempt
import br.com.vvrefeicoes.portal.domain.repository.RMP_REJECTION_REASON_MIN
import br.com.vvrefeicoes.portal.domain.repository.RmpRequestRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlinx.serialization.json.JsonObject

/** Máximo da observação/motivo aceito pela API. */
internal const val RMP_APPROVAL_NOTE_MAX = 2000

/**
 * Detalhe de um RMP aberto no portal: os campos preenchidos, a etapa que aguarda decisão e o histórico. Quem pode
 * decidir a etapa atual (a API diz em `canDecide`) aprova com uma observação opcional ou rejeita com o motivo
 * obrigatório; quem pode excluir (`canDelete`) exclui o RMP.
 */
class RmpApprovalController(
    private val repository: RmpRequestRepository,
    private val scope: CoroutineScope,
) {
    var approval by mutableStateOf<RmpApproval?>(null)
        private set

    var loading by mutableStateOf(false)
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    var note by mutableStateOf("")
        private set

    var deciding by mutableStateOf(false)
        private set

    private var requestId: String? = null

    fun load(id: String) {
        requestId = id
        approval = null
        note = ""
        errorMessage = null
        loading = true
        scope.launch {
            try {
                approval = repository.loadApproval(id)
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível carregar o RMP."
            } finally {
                loading = false
            }
        }
    }

    /** Prepara a decisão de um RMP sem carregar o detalhe (confirmação aberta direto da linha da tabela). */
    fun prepare(id: String) {
        requestId = id
        approval = null
        note = ""
        errorMessage = null
    }

    fun dismissError() {
        errorMessage = null
    }

    fun updateNote(value: String) {
        note = value.take(RMP_APPROVAL_NOTE_MAX)
    }

    /** Aprova a etapa atual e avisa [onDecided] (para a lista recarregar). */
    fun approve(onDecided: () -> Unit) = decide(approve = true, onDecided)

    /** Rejeita a etapa atual; o motivo é obrigatório, com o mesmo mínimo da API. */
    fun reject(onDecided: () -> Unit) {
        if (!canReject) {
            errorMessage = "Informe o motivo da rejeição com no mínimo $RMP_REJECTION_REASON_MIN caracteres."
            return
        }
        decide(approve = false, onDecided)
    }

    val canReject: Boolean
        get() = note.trim().length >= RMP_REJECTION_REASON_MIN

    /** Exclui o RMP (o botão só aparece quando a API diz `canDelete`) e avisa [onDeleted]. */
    fun delete(onDeleted: () -> Unit) {
        val id = requestId ?: return
        if (deciding) return
        deciding = true
        errorMessage = null
        scope.launch {
            try {
                repository.deleteSubmitted(id)
                onDeleted()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível excluir o RMP."
            } finally {
                deciding = false
            }
        }
    }

    fun downloadAttachment(attachment: RmpRequestAttachment) {
        val id = requestId ?: return
        scope.launch {
            try {
                savePdf(attachment.fileName, encodeBase64(repository.downloadApprovalAttachment(id, attachment.id)))
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível baixar o arquivo."
            }
        }
    }

    /**
     * Grava o checklist da transferência: sobe os PDFs escolhidos (ASO e assinatura do funcionário) e salva os itens e
     * a observação. [onSaved] fecha o checklist e recarrega a lista.
     */
    fun saveChecklist(
        items: Map<String, String>,
        observation: String,
        files: Map<String, List<PickedFile>>,
        onSaved: () -> Unit,
    ) {
        val id = requestId ?: return
        if (deciding) return
        deciding = true
        errorMessage = null
        scope.launch {
            try {
                files.forEach { (field, picked) ->
                    picked.forEach { file -> repository.uploadSubmittedAttachment(id, field, file.name, file.bytes) }
                }
                approval = repository.saveTransferChecklist(id, items, observation)
                onSaved()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                // Os PDFs que já subiram ficam no RMP: recarrega para a tela mostrar o que foi gravado.
                runCatching { approval = repository.loadApproval(id) }
                errorMessage = throwable.message ?: "Não foi possível salvar o checklist."
            } finally {
                deciding = false
            }
        }
    }

    /** Registra a avaliação de desempenho da mudança de cargo; [onSaved] fecha a avaliação e recarrega a lista. */
    fun saveEvaluation(
        data: JsonObject,
        onSaved: () -> Unit,
    ) {
        val id = requestId ?: return
        if (deciding) return
        deciding = true
        errorMessage = null
        scope.launch {
            try {
                approval = repository.saveEvaluation(id, data)
                onSaved()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível salvar a avaliação."
            } finally {
                deciding = false
            }
        }
    }

    /** Convoca um candidato para o teste prático; [onSaved] fecha a convocação e recarrega a lista. */
    fun convoke(
        data: JsonObject,
        onSaved: () -> Unit,
    ) = runAction("Não foi possível convocar o candidato.") {
        approval = repository.convokeTestCandidate(it, data)
        onSaved()
    }

    /** Registra o resultado do teste de um candidato ("aprovado" ou "reprovado"). */
    fun recordTestResult(
        attempt: RmpTestAttempt,
        result: String,
        onSaved: () -> Unit,
    ) = runAction("Não foi possível registrar o resultado.") {
        approval = repository.recordTestResult(it, attempt.id, result)
        onSaved()
    }

    /** Admin reabre o RMP removendo as decisões escolhidas. */
    fun reopen(
        decisionIds: List<String>,
        onSaved: () -> Unit,
    ) = runAction("Não foi possível reabrir o RMP.") {
        approval = repository.reopen(it, decisionIds)
        onSaved()
    }

    /** Substitui o candidato da admissão concluída. */
    fun replaceCandidate(
        fields: Map<String, String>,
        reason: String,
        onSaved: () -> Unit,
    ) = runAction("Não foi possível substituir o candidato.") {
        approval = repository.replaceCandidate(it, fields, reason)
        onSaved()
    }

    /** Marca o RMP concluído como "Apto". */
    fun markApt(onSaved: () -> Unit) =
        runAction("Não foi possível marcar o RMP como Apto.") {
            approval = repository.markApt(it)
            onSaved()
        }

    /** Abre o documento RMP numa nova aba (para imprimir ou salvar em PDF). */
    fun openDocument() {
        val id = requestId ?: return
        val number =
            approval
                ?.request
                ?.number
                ?.toString()
                .orEmpty()
        scope.launch {
            try {
                openFileInBrowser("rmp-$number.html", "text/html", repository.downloadDocument(id))
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível abrir o documento RMP."
            }
        }
    }

    /** Abre o documento de convocação do candidato numa nova aba (para imprimir). */
    fun openConvocation(attempt: RmpTestAttempt) {
        val id = requestId ?: return
        scope.launch {
            try {
                val name = attempt.data["conv_nome_candidato"].orEmpty().ifBlank { "candidato" }
                openFileInBrowser("convocacao-$name.html", "text/html", repository.downloadConvocation(id, attempt.id))
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível abrir a convocação."
            }
        }
    }

    private fun runAction(
        failure: String,
        block: suspend (String) -> Unit,
    ) {
        val id = requestId ?: return
        if (deciding) return
        deciding = true
        errorMessage = null
        scope.launch {
            try {
                block(id)
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: failure
            } finally {
                deciding = false
            }
        }
    }

    /** Remove um anexo do RMP (ex.: um ASO do checklist) e recarrega o detalhe. */
    fun removeAttachment(attachment: RmpRequestAttachment) {
        val id = requestId ?: return
        if (deciding) return
        deciding = true
        errorMessage = null
        scope.launch {
            try {
                repository.deleteSubmittedAttachment(id, attachment.id)
                approval = repository.loadApproval(id)
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível remover o arquivo."
            } finally {
                deciding = false
            }
        }
    }

    private fun decide(
        approve: Boolean,
        onDecided: () -> Unit,
    ) {
        val id = requestId ?: return
        if (deciding) return
        deciding = true
        errorMessage = null
        scope.launch {
            try {
                approval = if (approve) repository.approve(id, note) else repository.reject(id, note)
                note = ""
                onDecided()
            } catch (exception: CancellationException) {
                throw exception
            } catch (throwable: Throwable) {
                errorMessage = throwable.message ?: "Não foi possível registrar a decisão."
            } finally {
                deciding = false
            }
        }
    }
}

@Composable
fun rememberRmpApprovalController(): RmpApprovalController {
    val appGraph = LocalAppGraph.current
    val scope = rememberCoroutineScope()
    return remember { RmpApprovalController(appGraph.rmpRequestRepository, scope) }
}
